import React, {useState, useCallback, useMemo} from 'react';
import {Backdrop, Box, CircularProgress, Grid, Typography, Tooltip} from '@mui/material';
import {grey} from '@mui/material/colors';
import {useMutation, useQuery} from "@apollo/client";
import moment from "moment";

import {
    UPDATE_PERFORMER_SHIFT_SELECTION,
    DELETE_SHIFT,
    QUERY_ALL_PERFORMERS_AND_FILTER_BY_SERVICE_TYPE
} from "../../queries/GraphQLQueries";
import {
    SHIFT_PLAN_STATUS_TYPES,
    SHIFT_PLAN_TIMEBLOCK_MIN_WIDTH_REM,
    SHIFT_PLAN_TIMEBLOCK_WIDTH_VW,
    SHIFT_SELECTION_STATUS
} from "../../domain/ShiftPlanConstants";
import {
    SHIFT_PLAN_ERROR_MESSAGE,
    SHIFT_PLAN_STATUS_COLORS,
    isShiftPlanAfterCurrentTime,
    isTimeblockOverlappingWindow,
    determineIsPerformerSelected,
    renderPartialServiceTimeblockBackground,
    getPerformerAccreditedServiceTypes,
    formatTimeToUtc,
    calculateScheduledAndAvailableHours,
} from "../../helpers/ShiftPlanHelper";
import CustomAlert from "../CustomAlert";
import DeleteShiftInlineDialog from "./DeleteShiftInlineDialog";
import ShiftPlanTimeblockHeader from "./ShiftPlanTimeblockHeader";
import ShiftPlanTimeblockRow from "./ShiftPlanTimeblockRow";
import ShiftPlanGridHeader from "./ShiftPlanGridHeader";
import "../../css/Schedule.css";

const performerNameMinWidthRem = 9.5;
const performerNameWidthVw = 66/7;
const rowHeight = '1.5rem';
const headerRowHeight = '2.2rem';

const FULLY_OPAQUE = 1;
const SLIGHTLY_TRANSPARENT = 0.6;

const styles = {
    grid: {
        container: {
            display: 'flex',
            maxHeight: '100%',
            width: 'fit-content'
        },
        dimensions: {
            minWidth: (timeblockCount) => `max(${performerNameMinWidthRem + (timeblockCount * SHIFT_PLAN_TIMEBLOCK_MIN_WIDTH_REM)}rem,
             ${performerNameWidthVw + (timeblockCount * SHIFT_PLAN_TIMEBLOCK_WIDTH_VW)}vw)`
        },
        scrollableContainer: {
            calculatedWidth: (timeblockCount) => {
                const vwWidth = `calc(${performerNameWidthVw + (timeblockCount * SHIFT_PLAN_TIMEBLOCK_WIDTH_VW)}vw)`;
                const minRemWidth = `${performerNameMinWidthRem + (timeblockCount * SHIFT_PLAN_TIMEBLOCK_MIN_WIDTH_REM)}rem`;
                return `max(${vwWidth}, ${minRemWidth})`;
            }
        }
    },
    cell: {
        base: {
            width: `${SHIFT_PLAN_TIMEBLOCK_WIDTH_VW}vw`,
            height: rowHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '0.0625rem solid #ccc',
            backgroundColor: 'white',
            boxSizing: 'border-box',
            marginBottom: '0.25rem',
            minWidth: `${SHIFT_PLAN_TIMEBLOCK_MIN_WIDTH_REM}rem`,
            cursor: 'pointer',
            padding: '0.125rem'
        },
        performer: {
            width: `${performerNameWidthVw}vw`,
            height: rowHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            border: '0.0625rem solid #ccc',
            backgroundColor: 'white',
            boxSizing: 'border-box',
            marginBottom: '0.25rem',
            minWidth: `${performerNameMinWidthRem}rem`,
            padding: '0.125rem',
            paddingLeft: '0.5rem',
            position: 'relative',
            cursor: 'default'
        },
        header: {
            width: `${SHIFT_PLAN_TIMEBLOCK_WIDTH_VW}vw`,
            height: headerRowHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '0.0625rem solid #ccc',
            boxSizing: 'border-box',
            marginBottom: '0.25rem',
            minWidth: `${SHIFT_PLAN_TIMEBLOCK_MIN_WIDTH_REM}rem`,
            padding: '0.125rem',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            fontWeight: 'bold',
            backgroundColor: grey[100],
            cursor: 'default'
        },
        performerHeader: {
            width: `${performerNameWidthVw}vw`,
            height: headerRowHeight,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '0.0625rem solid #ccc',
            boxSizing: 'border-box',
            marginBottom: '0.25rem',
            minWidth: `${performerNameMinWidthRem}rem`,
            padding: '0.125rem',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            fontWeight: 'bold',
            backgroundColor: grey[100],
            cursor: 'default'
        },
        availableHours: {
            position: 'absolute',
            right: '0.1rem',
            color: grey[600],
            fontSize: '0.75rem',
            fontWeight: 'normal',
        }
    },
    row: {
        header: {
            position: 'sticky',
            top: 58,
            zIndex: 10
        },
        performer: {
            display: 'flex'
        },
        performerName: {
            maxWidth: 'calc(100% - 3.8rem)',
            position: 'absolute',
            left: '0.1rem'
        }
    },
    rowContainer: {
        flexWrap: 'nowrap'
    },
    loading: {
        backdrop: {
            color: '#fff',
            display: 'flex',
            zIndex: 1500
        }
    }
};

const ShiftPlanTimeblockGrid = ({
    performerEnrollments,
    timeblocks,
    availableServiceTypes,
    isReadOnly = false,
    selectedDate,
    shiftPlan,
    bookableServiceTypes = [],
    setBookableServiceTypes
}) => {
    const [selectedTimeblocks, setSelectedTimeblocks] = useState({});
    const [alertMessage, setAlertMessage] = useState(null);
    const [isSelectingTimeblock, setIsSelectingTimeblock] = useState(false);
    const [openDeleteShiftDialog, setOpenDeleteShiftDialog] = useState(false);
    const [shiftToBeDeleted, setShiftToBeDeleted] = useState(null);
    const [performerName, setPerformerName] = useState(null);

    const handleCloseCustomAlert = useCallback(() => setAlertMessage(null), []);

    const calculatedWidth = useMemo(() =>
        styles.grid.scrollableContainer.calculatedWidth(timeblocks.length),
        [timeblocks.length]
    );

    const timeblockGridStyle = {
        minWidth: styles.grid.dimensions.minWidth(timeblocks.length)
    };

    const serviceTypeKeycodes = [...new Set(availableServiceTypes?.map(service => service.serviceKeycode))];

    const {data: performersData, loading: isLoadingAccreditationData} = useQuery(QUERY_ALL_PERFORMERS_AND_FILTER_BY_SERVICE_TYPE, {
        variables: {serviceTypeKeycodes}, fetchPolicy: "network-only",
    });

    const buildTimeblockCellStyle = (performer, timeblock, allServices) => {
        const isTimeblockPartiallyOrFullyApproved = isTimeblockOverlappingWindow(timeblock, performer.approvedFromTime, performer.approvedToTime);
        const isOverlappingShift = isTimeblockOverlappingWindow(timeblock, performer.shift?.performingFrom, performer.shift?.to);
        const isOverlappingEnrollmentWindow = isTimeblockOverlappingWindow(timeblock, performer.enrollmentFromTime, performer.enrollmentToTime);
        const accreditedServiceTypes = getPerformerAccreditedServiceTypes(performer.id, performersData);

        let background = SHIFT_PLAN_STATUS_COLORS.PERFORMER_NOT_AVAILABLE_COLOR;
        let unAccreditedServiceOverlay = null;

        if (isOverlappingEnrollmentWindow) {
            const performerStatusColors = [];

            if (isOverlappingShift) {
                performerStatusColors.push({
                    statusType: SHIFT_PLAN_STATUS_TYPES.GENERATED,
                    color: SHIFT_PLAN_STATUS_COLORS.GENERATED_SHIFT_DARK_GOLD_COLOR,
                    startTime: performer.shift?.performingFrom,
                    endTime: performer.shift?.to
                });
            }

            if (isTimeblockPartiallyOrFullyApproved) {
                performerStatusColors.push({
                    statusType: SHIFT_PLAN_STATUS_TYPES.SELECTED,
                    startTime: performer.approvedFromTime,
                    endTime: performer.approvedToTime
                });
            }

            const timeblockStyling = renderPartialServiceTimeblockBackground(
                timeblock.fromTimeUtc || timeblock.fromTime,
                timeblock.toTimeUtc || timeblock.toTime,
                allServices,
                performer,
                performerStatusColors,
                accreditedServiceTypes
            );
            background = timeblockStyling.background;
            unAccreditedServiceOverlay = timeblockStyling.unaccreditedOverlays;
        }

        return {
            style: {
                ...styles.cell.base,
                background,
                cursor: isReadOnly ? 'not-allowed' : 'pointer',
                opacity: isReadOnly ? SLIGHTLY_TRANSPARENT : FULLY_OPAQUE,
                position: 'relative'
            },
            unAccreditedServiceOverlay
        };
    };

    const refetchDetailedShiftPlans = {refetchQueries: ['GetDetailedShiftPlansForTimeRange']};
    const [updatePerformerShiftSelection] = useMutation(UPDATE_PERFORMER_SHIFT_SELECTION, refetchDetailedShiftPlans);
    const [deleteShift] = useMutation(DELETE_SHIFT, refetchDetailedShiftPlans);

    const handleShiftDelete = () => {
        setAlertMessage({ children: 'Deleting Shift...', severity: 'success' });

        deleteShift({
            variables: {
                shiftId: shiftToBeDeleted
            }
        })
        .then(() => {
            setAlertMessage({ children: 'Shift Deleted.', severity: 'success' });
            setSelectedTimeblocks({});
        })
        .catch(error => {
            setAlertMessage({ children: error?.message, severity: 'error' });
            console.log("Delete shift error:", error);
        })
        .finally(() => setOpenDeleteShiftDialog(false));
    };

    const handlePerformerShiftSelection = (performer) => {
        if (isReadOnly) {
            setAlertMessage({children: SHIFT_PLAN_ERROR_MESSAGE.NO_PERFORMERS_AVAILABLE, severity: 'error'});
            return;
        }

        if (!isShiftPlanAfterCurrentTime(performer.shiftStartTime)) {
            setAlertMessage({children: SHIFT_PLAN_ERROR_MESSAGE.CANNOT_MODIFY_PAST_SHIFT_PLAN, severity: 'error'});
            return;
        }

        if (performer.shiftSelectionStatus === SHIFT_SELECTION_STATUS.CONFLICT && performer.shift !== null) {
            setAlertMessage({children: SHIFT_PLAN_ERROR_MESSAGE.PERFORMER_HAS_CONFLICTED_SHIFT_PLAN, severity: 'error'});
            return;
        }

        const isPerformerSelected = determineIsPerformerSelected(performer);

        setIsSelectingTimeblock(true);

        updatePerformerShiftSelection({
            variables: {
                shiftPlanApprovals: [
                    {
                        shiftPlanId: performer.shiftPlanId,
                        fromTime: formatTimeToUtc(performer.enrollmentFromTime),
                        toTime: formatTimeToUtc(performer.enrollmentToTime),
                    }
                ],
                performerId: performer.id,
                isPerformerSelected
            }
        })
            .catch(error => {
                setAlertMessage({children: error?.message, severity: 'error'});
                console.log("error " + error)
            })
            .finally(() => setIsSelectingTimeblock(false));
    };

    const handleTimeblockAddition = (performer, timeblock, key) => {
        setSelectedTimeblocks(prev => ({...prev, [key]: true}));

        setIsSelectingTimeblock(true);
        const performerShiftWithinTimeblock = {
            shiftStarts: moment.utc(timeblock.fromTime).isAfter(performer.enrollmentFromTime)
                ? moment.utc(timeblock.fromTime)
                : performer.enrollmentFromTime,

            shiftEnds: moment.utc(timeblock.toTime).isBefore(performer.enrollmentToTime)
                ? moment.utc(timeblock.toTime)
                : performer.enrollmentToTime
        };

        updatePerformerShiftSelection({
            variables: {
                shiftPlanApprovals: [{
                    shiftPlanId: performer.shiftPlanId,
                    fromTime: performerShiftWithinTimeblock.shiftStarts,
                    toTime: performerShiftWithinTimeblock.shiftEnds,
                }],
                performerId: performer.id,
                isPerformerSelected: true
            }
        })
            .catch(error => {
                setAlertMessage({children: error?.message, severity: 'error'});
                console.log("error " + error)
            })
            .finally(() => setIsSelectingTimeblock(false));
    };

    const handleTimeblockRemoval = (performer, timeblock, key) => {
        const isApprovedStartTimeMissing = !performer.approvedFromTime;
        const isApprovedEndTimeMissing = !performer.approvedToTime;
        if (isApprovedStartTimeMissing || isApprovedEndTimeMissing) {
            return;
        }

        setSelectedTimeblocks(prev => ({...prev, [key]: false}));

        setIsSelectingTimeblock(true);
        const performerShiftWithinTimeblock = {
            shiftStarts: moment.utc(timeblock.fromTime).isAfter(performer.approvedFromTime)
                ? moment.utc(timeblock.fromTime)
                : performer.approvedFromTime,

            shiftEnds: moment.utc(timeblock.toTime).isBefore(performer.approvedToTime)
                ? moment.utc(timeblock.toTime)
                : performer.approvedToTime
        };

        updatePerformerShiftSelection({
            variables: {
                shiftPlanApprovals: [{
                    shiftPlanId: performer.shiftPlanId,
                    fromTime: performerShiftWithinTimeblock.shiftStarts,
                    toTime: performerShiftWithinTimeblock.shiftEnds,
                }],
                performerId: performer.id,
                isPerformerSelected: false
            }
        })
            .catch(error => {
                setAlertMessage({children: error?.message, severity: 'error'});
                console.log("error " + error)
            })
            .finally(() => setIsSelectingTimeblock(false));
    };

    const handlePerformerShiftSelectionByIndividualTimeBlock = (performer, timeblock) => {
        if (isReadOnly) {
            setAlertMessage({children: SHIFT_PLAN_ERROR_MESSAGE.NO_PERFORMERS_AVAILABLE, severity: 'error'});
            return;
        }

        const displayTime = timeblock.displayTime;
        const key = `${performer.id}_${performer.shiftPlanId}_${displayTime}`;

        if (!isShiftPlanAfterCurrentTime(performer.shiftStartTime)) {
            setAlertMessage({children: SHIFT_PLAN_ERROR_MESSAGE.CANNOT_MODIFY_PAST_SHIFT_PLAN, severity: 'error'});
            return;
        }

        const isOverlappingShift = isTimeblockOverlappingWindow(timeblock, performer.shift?.performingFrom, performer.shift?.to);

        if (isOverlappingShift) {
            setPerformerName(performer.name);
            setShiftToBeDeleted(performer.shift.id);
            setOpenDeleteShiftDialog(true);
            return;
        }

        if (performer.shiftSelectionStatus === SHIFT_SELECTION_STATUS.CONFLICT && performer.shift === null) {
            setAlertMessage({children: SHIFT_PLAN_ERROR_MESSAGE.PERFORMER_HAS_CONFLICTED_SHIFT_PLAN, severity: 'error'});
            return;
        }

        const isOverlappingEnrollmentWindow = isTimeblockOverlappingWindow(timeblock, performer.enrollmentFromTime, performer.enrollmentToTime);

        if (!isOverlappingEnrollmentWindow) {
            setAlertMessage({children: SHIFT_PLAN_ERROR_MESSAGE.PERFORMER_NOT_AVAILABLE, severity: 'error'});
            return;
        }

        const isCurrentlySelected = selectedTimeblocks[key] ?? false;
        const isTimeblockCurrentlyApproved = isTimeblockOverlappingWindow(timeblock, performer.approvedFromTime, performer.approvedToTime);

        const shouldAddTimeblock = !isTimeblockCurrentlyApproved && !isCurrentlySelected;
        const shouldRemoveTimeblock = isTimeblockCurrentlyApproved || isCurrentlySelected;

        if (shouldAddTimeblock) {
            handleTimeblockAddition(performer, timeblock, key);
        } else if (shouldRemoveTimeblock) {
            handleTimeblockRemoval(performer, timeblock, key);
        }
    };

    const getPerformerCellDynamicStyle = () => ({
        ...styles.cell.performer,
        cursor: isReadOnly ? 'not-allowed' : 'pointer',
        opacity: isReadOnly ? SLIGHTLY_TRANSPARENT : FULLY_OPAQUE,
    });

    return (
        <>
            <ShiftPlanGridHeader
                shiftPlan={shiftPlan}
                bookableServiceTypes={bookableServiceTypes}
                setBookableServiceTypes={setBookableServiceTypes}/>

            <Box sx={{...styles.grid.container,width: calculatedWidth}}>

                <Box sx={{flex: 1, paddingBottom: '0.6rem'}}>
                    <Grid container direction="column" sx={timeblockGridStyle}>
                        <Grid item sx={styles.row.header}>
                            <Grid container sx={styles.rowContainer}>
                                <Grid item sx={styles.cell.performerHeader}>
                                    <Typography variant="body2">Performers</Typography>
                                </Grid>
                                <ShiftPlanTimeblockHeader
                                    timeblocks={timeblocks}
                                    performerEnrollments={performerEnrollments}
                                    selectedDate={selectedDate}
                                    serviceTypePlans={shiftPlan.bookableServiceTypePlans} />
                            </Grid>
                        </Grid>

                        {performerEnrollments.map((performer, rowIdx) => (
                            <Grid item key={rowIdx} sx={styles.row.performer}>
                                <Box sx={getPerformerCellDynamicStyle()} onClick={() => handlePerformerShiftSelection(performer)}>
                                    <Tooltip title={performer.name} placement="left">
                                        <Typography variant="body2" noWrap sx={styles.row.performerName}>{performer.name}</Typography>
                                    </Tooltip>
                                    {
                                        !isReadOnly
                                        &&
                                        <Typography variant="body2" sx={styles.cell.availableHours}>
                                            ({calculateScheduledAndAvailableHours(performer)})
                                        </Typography>
                                    }
                                </Box>
                                <Grid container sx={styles.rowContainer}>
                                    <ShiftPlanTimeblockRow
                                        timeblocks={timeblocks}
                                        performer={performer}
                                        availableServiceTypes={availableServiceTypes}
                                        buildTimeblockCellStyle={buildTimeblockCellStyle}
                                        handlePerformerShiftSelectionByIndividualTimeBlock={handlePerformerShiftSelectionByIndividualTimeBlock}
                                    />
                                </Grid>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Box>

            <Backdrop open={isSelectingTimeblock || isLoadingAccreditationData} sx={styles.loading.backdrop}>
                <CircularProgress color="inherit"/>
            </Backdrop>

            <CustomAlert alertMessage={alertMessage} handleCloseCustomAlert={handleCloseCustomAlert}/>

            <DeleteShiftInlineDialog
                openDeleteShiftDialog={openDeleteShiftDialog}
                handleClose={() => setOpenDeleteShiftDialog(false)}
                handleShiftDelete={handleShiftDelete}
                performerName={performerName} />
        </>
    );
};

export default ShiftPlanTimeblockGrid;